// First: the pay-now link leaves the URL before Sentry, Hotjar or the app can read it.
import 'src/utils/takePayNowFragment';

import { StrictMode } from 'react';

import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';

// Self-hosted brand font; replaces the Google Fonts <link> so no user IP leaks to Google and CSP stays strict.
import '@fontsource-variable/rethink-sans';
import '@fontsource-variable/rethink-sans/wght-italic.css';

import { tracesSampler } from 'src/hooks/sse/streamTiming';
import { tvDashboardFromPath } from 'src/tv/pairingCode';
import TvApp from 'src/tv/TvApp';
import { LoggedError } from 'src/utils/logger';
import { isStaleChunkReloadPending, registerStaleChunkReload } from 'src/utils/reloadOnStaleChunk';
import { dropNavigationFragments, dropUrlFragment, IGNORED_ERRORS, DENIED_URLS } from 'src/utils/sentryFilters';

// Only Sentry and the TV app are imported statically. The agent app (src/AgentRoot.tsx) is loaded
// with import() below, so a TV never downloads or evaluates it.

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  ignoreErrors: IGNORED_ERRORS,
  denyUrls: DENIED_URLS,
  // Only the SSE wait times in src/hooks/sse/streamTiming.ts. No browserTracingIntegration: its page load and
  // navigation spans carry raw urls (customer ids, search terms) and a span per query-string change.
  tracesSampler,
  // The SSE timings keep only the user agent (Dial Maker desktop vs browser): no page url, referrer or
  // breadcrumbs, which hold customer ids, search terms and PNIs. Error events keep all of it.
  beforeSendTransaction: (event) => ({
    ...event,
    request: { headers: { 'User-Agent': event.request?.headers?.['User-Agent'] } },
    breadcrumbs: undefined,
  }),
  beforeBreadcrumb: dropNavigationFragments,
  integrations: [
    // Capture unexpected console.error from third-party libs / React internals.
    // Duplicates from logger.error() are filtered in beforeSend via LoggedError instanceof check.
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ],
  beforeSend(event, hint) {
    const error = hint?.originalException;

    // Drop duplicates from captureConsoleIntegration for errors already reported by logger.error().
    // logger.error() wraps its console.error() output in LoggedError so we can identify it here.
    if (error instanceof LoggedError)
      return null;

    // A stale-chunk reload is already recovering the page; its rethrown import error is noise.
    if (isStaleChunkReloadPending())
      return null;

    // Enrich [object Object] events with a tag so they're easy to find and investigate.
    // These come from console.error() with non-serializable objects (HttpErrorResponse etc.).
    // We keep them (something wrong is happening) but tag them for filtering in Sentry UI.
    if (event.message && event.message.includes('[object Object]'))
      event.tags = { ...event.tags, unserializedError: 'true' };

    return dropUrlFragment(event);
  },
});

registerStaleChunkReload();

const root = ReactDOM.createRoot(document.getElementById('root')!);

// Wall screens (/mb-dash, /kh-dash, /calls-map, /web-traffic, /live-map) authenticate with their own TV token, so they skip Auth0 and
// the whole agent provider stack.
const tvDashboard = tvDashboardFromPath(window.location.pathname);

if (tvDashboard)
  root.render(
    <StrictMode>
      <TvApp dashboard={tvDashboard} />
    </StrictMode>
  );
else
  // Agent modules read storage at load, so the storage shim must run first. The bundler evaluates
  // chunks in its own order, so only a separate import() guarantees that (src/tv/CLAUDE.md).
  import('src/utils/overwriteStorageIfNeeded')
    .then(() => import('src/AgentRoot'))
    .then(({ default: AgentRoot }) =>
      root.render(
        <StrictMode>
          <AgentRoot />
        </StrictMode>
      )
    );
